/**
 * Header workspace selector (org+workspace sessions).
 *
 * Behavior contract (extension of designs/admin-mfa-org-sessions.md):
 * - Hidden until an organization scope is active (workspaces belong to an org).
 * - Options come from GET /v1/directory/workspaces under the CURRENT org
 *   scope (server-authorized via the verified `orgId` claim; the response
 *   contains only org workspaces).
 * - Selecting dispatches `switchWorkspace` → POST /auth/sessions/org
 *   {orgId, workspaceId}. The server verifies the workspace belongs to the
 *   org before minting the `workspaceId` claim; the client never trusts its
 *   own workspaceId for access.
 * - States: loading / empty / error / switching — mirrors OrgSelector. Old
 *   tokens/UI persist until the thunk's success branch adopts the new pair
 *   and resets the RTK Query cache.
 * - When the auto-cascade already picked the only workspace, this just shows
 *   the active label (and lets the user switch to another one if the org
 *   gains workspaces later).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Loader2, Archive, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useGetWorkspacesQuery } from '@/store/api/directoryApi';
import { switchWorkspace, clearOrgError } from '@/store/auth/orgSessionSlice';

const OPTION_CLASS =
  'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent focus:bg-accent focus:outline-none';

export function WorkspaceSelector() {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);

  const activeOrgId = useAppSelector((s) => s.orgSession.activeOrgId);
  const activeWorkspaceId = useAppSelector((s) => s.orgSession.activeWorkspaceId);
  const activeWorkspaceName = useAppSelector((s) => s.orgSession.activeWorkspaceName);
  const switching = useAppSelector((s) => s.orgSession.switching);
  const switchError = useAppSelector((s) => s.orgSession.error);

  const {
    data: page,
    isLoading,
    isError,
    refetch,
  } = useGetWorkspacesQuery(undefined, {
    // Don't hit the network until the user opens the popover.
    skip: !open,
  });

  const workspaces = useMemo(() => page?.data ?? [], [page]);
  const query = search.trim().toLowerCase();
  const visible = useMemo(
    () => workspaces.filter((w) => w.name.toLowerCase().includes(query)),
    [workspaces, query],
  );

  // Keep the keyboard cursor in range as the filtered list changes.
  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(0, visible.length - 1)));
  }, [visible.length]);

  // Focus the search field whenever the popover opens.
  useEffect(() => {
    if (open) {
      setSearch('');
      setActiveIndex(0);
      const t = setTimeout(() => searchRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Close the popover once a switch starts (same UX as OrgSelector).
  useEffect(() => {
    if (switching) setOpen(false);
  }, [switching]);

  const handleSelect = (id: string, name: string) => {
    if (switching || id === activeWorkspaceId) return;
    void dispatch(switchWorkspace({ workspaceId: id, workspaceName: name }));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (visible.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % visible.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + visible.length) % visible.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const w = visible[activeIndex];
      if (w) handleSelect(w.id, w.name);
    }
  };

  // No org scope yet: workspaces are meaningless — the OrgSelector is the
  // visible affordance in that state.
  if (!activeOrgId) return null;

  const needsChoice = !activeWorkspaceId && !switching;
  const label = switching
    ? 'Switching…'
    : (activeWorkspaceName ?? (activeWorkspaceId ? 'Workspace' : 'No workspace'));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'max-w-[200px] gap-1.5',
            needsChoice && 'text-amber-600 dark:text-amber-400',
          )}
          aria-label="Switch workspace"
          disabled={switching}
        >
          <Archive className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="truncate">{label}</span>
          {switching ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
          ) : (
            <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-2" onKeyDown={handleKeyDown}>
        <div className="flex flex-col gap-2">
          <Input
            ref={searchRef}
            placeholder="Search workspaces…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search workspaces"
            role="combobox"
            aria-expanded={open}
            aria-controls="workspace-listbox"
            aria-activedescendant={`workspace-option-${visible[activeIndex]?.id ?? 'none'}`}
          />
          {switchError && (
            <div
              role="alert"
              className="flex items-start gap-1.5 rounded border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive"
            >
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="min-w-0 break-words">{switchError}</span>
              <button
                type="button"
                className="ml-auto shrink-0 underline"
                onClick={() => dispatch(clearOrgError())}
                aria-label="Dismiss workspace switch error"
              >
                dismiss
              </button>
            </div>
          )}
          <div
            id="workspace-listbox"
            role="listbox"
            aria-label="Workspaces"
            className="max-h-64 overflow-y-auto"
          >
            {isLoading && (
              <div
                className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground"
                data-testid="workspace-list-loading"
              >
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Loading workspaces…
              </div>
            )}
            {isError && !isLoading && (
              <div
                role="alert"
                className="flex flex-col items-center gap-2 py-6 text-sm text-muted-foreground"
                data-testid="workspace-list-error"
              >
                <AlertCircle className="h-4 w-4 text-destructive" aria-hidden />
                <span>Could not load workspaces.</span>
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  Retry
                </Button>
              </div>
            )}
            {!isLoading && !isError && visible.length === 0 && (
              <p
                className="py-6 text-center text-sm text-muted-foreground"
                data-testid="workspace-list-empty"
              >
                {workspaces.length ? 'No workspaces match.' : 'No workspaces available.'}
              </p>
            )}
            {!isLoading &&
              !isError &&
              visible.map((w, idx) => {
                const isActive = w.id === activeWorkspaceId;
                return (
                  <button
                    key={w.id}
                    type="button"
                    id={`workspace-option-${w.id}`}
                    role="option"
                    aria-selected={isActive}
                    disabled={switching}
                    data-active={isActive ? 'true' : undefined}
                    className={cn(
                      OPTION_CLASS,
                      activeIndex === idx && 'bg-accent',
                      switching && 'cursor-not-allowed opacity-60',
                    )}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => handleSelect(w.id, w.name)}
                  >
                    <Check
                      className={cn('h-4 w-4 shrink-0', isActive ? 'opacity-100' : 'opacity-0')}
                      aria-hidden
                    />
                    <span className="truncate">{w.name}</span>
                    <span className="ml-auto shrink-0 text-xs text-muted-foreground">{w.kind}</span>
                    {switching && isActive && (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                    )}
                  </button>
                );
              })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
